// =============================================================================
// PokerTable.tsx — Main poker table component
// Project: Texas Hold'em Social Casino
// =============================================================================
// Renders the poker table with player seats, community cards, pot,
// action buttons, chat, and hand result overlay.
//
// Seat positions are calculated based on player count (2, 6, or 9-max)
// to keep the table balanced and readable at all screen sizes.

import React, { useEffect } from 'react'
import { useGameStore } from '../store/game-store'
import { usePlayerStore } from '../store/player-store'
import { useUIStore } from '../store/ui-store'
import { Player, ActionType } from '../types'
import CardComponent from './Card'
import ActionButtons from './ActionButtons'
import SeatComponent from './Seat'
import PotDisplay from './PotDisplay'
import ChatOverlay from './ChatOverlay'
import HandResultOverlay from './HandResultOverlay'
import TableControls from './TableControls'

// Seat position calculator — returns percentage positions for each seat
// based on table size (2, 6, or 9 players).
function getSeatPositions(tableSize: number): { x: number; y: number }[] {
  const positions: Record<number, { x: number; y: number }[]> = {
    2: [
      { x: 50, y: 85 },
      { x: 50, y: 15 },
    ],
    6: [
      { x: 50, y: 88 },
      { x: 85, y: 60 },
      { x: 75, y: 18 },
      { x: 25, y: 18 },
      { x: 15, y: 60 },
    ],
    9: [
      { x: 50, y: 88 },
      { x: 82, y: 75 },
      { x: 88, y: 50 },
      { x: 82, y: 25 },
      { x: 50, y: 12 },
      { x: 18, y: 25 },
      { x: 12, y: 50 },
      { x: 18, y: 75 },
      { x: 50, y: 88 },
    ],
  }
  return positions[tableSize] || positions[6]
}

export default function PokerTable(): React.ReactElement {
  const { gameState, isMyTurn, availableActions, lastHandResult, handAnimationActive, clearHandResult, isConnected, ws, connectToTable, tableId, disconnectFromTable } = useGameStore()
  const { profile } = usePlayerStore()
  const { isMobile, navigateTo } = useUIStore()

  // Auto-reconnect if not connected but we have a token
  useEffect(() => {
    if (!ws && !tableId && profile) {
      const token = profile.id
      const name = profile.name
      // Try to create a table first, then connect
      fetch(`${import.meta.env.VITE_API_URL || '/api'}/matchmaking/quick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tableSize: 6, stakeLevel: 'micro' }),
      }).then(r => r.json()).then(json => {
        if (json.success) {
          connectToTable(json.data.tableId, token, name, token, 6, 'micro')
        }
      }).catch(() => {})
    }
  }, [])

  if (!gameState) {
    return (
      <div className="flex-center" style={{ flex: 1, flexDirection: 'column', gap: 12 }}>
        <div>Connecting to table...</div>
        <button className="btn btn-secondary" onClick={() => navigateTo('lobby')}>
          Back to Lobby
        </button>
      </div>
    )
  }

  const seatPositions = getSeatPositions(gameState.players.length)

  // Sort players by seat index for consistent positioning
  const seatedPlayers = [...gameState.players].sort((a, b) => a.seatIndex - b.seatIndex)

  // Find my player
  const myPlayer = gameState.players.find(p => p.id === profile?.id)
  const mySeatIndex = myPlayer?.seatIndex ?? 0

  return (
    <div className="flex-center" style={{ flex: 1, flexDirection: 'column', background: '#0a0a1a', position: 'relative' }}>
      {/* Header */}
      <TableControls />

      <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}>
        <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 11 }} onClick={() => { disconnectFromTable(); navigateTo('lobby') }}>
          Leave
        </button>
      </div>

      {/* Table */}
      <div className="table" style={{ width: isMobile ? '95%' : '85%', maxWidth: 600, minHeight: isMobile ? 340 : 460 }}>
        <div className="table-felt" />

        {/* Player Seats */}
        {seatedPlayers.map((player, i) => {
          const pos = seatPositions[i] || seatPositions[0]
          const isMe = player.id === profile?.id
          const isCurrent = gameState.currentPlayerIndex === gameState.players.indexOf(player)
          const rotation = isMobile ? 2 : 2

          return (
            <SeatComponent
              key={player.id}
              player={player}
              x={pos.x}
              y={pos.y}
              isMe={isMe}
              isCurrent={isCurrent}
              isDealer={gameState.dealerIndex === gameState.players.indexOf(player)}
              mySeatIndex={mySeatIndex}
              totalPlayers={seatedPlayers.length}
            />
          )
        })}

        {/* Community Cards */}
        <div className="community-cards">
          {gameState.communityCards.length === 0 && gameState.phase !== 'preflop' && (
            <span style={{ color: 'rgba(255,255,255,0.15)', fontSize: 13 }}>Waiting for flop...</span>
          )}
          {gameState.communityCards.map((card, i) => (
            <CardComponent key={`community-${i}`} card={card} size="large" animated />
          ))}
        </div>

        {/* Pot */}
        <PotDisplay amount={gameState.pot} />
      </div>

      {/* Action Buttons (bottom bar) */}
      <ActionButtons
        isMyTurn={isMyTurn}
        actions={availableActions}
        currentBet={gameState.currentBet}
        myPlayer={myPlayer}
        minRaise={gameState.minRaise}
      />

      {/* Chat Overlay */}
      <ChatOverlay />

      {/* Hand Result */}
      {handAnimationActive && lastHandResult && (
        <HandResultOverlay result={lastHandResult} onDismiss={clearHandResult} />
      )}
    </div>
  )
}
